import AppContext from 'App/AppContext';
import { IPIconButton, IPScrollablePane, IPSeparator, IPSpinner, IPText, IPTooltipHost } from 'baseComponents';
import { IPStack } from 'baseComponents/Stack/IPStack';
import GenericModal from 'components/Dialogs/GenericModal';
import ArrayViewer from 'components/Utils/ArrayViewer';
import { JSONViewer } from 'components/Utils/JSONViewer';
import { globalStackTokensGapMedium, globalTextStylesBold, refreshIcon } from 'globalStyles';
import KPIDataTransform from 'models/kpi/kpiDataTransform';
import KPIDataTransformResponse from 'models/kpi/kpiDataTransformResponse';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { KeyPath } from 'react-json-tree';
import { apiApplyKPIDataTransform } from 'services/Api/kpiService';
import { apiRequest } from 'services/Auth/authConfig';
import { IPSpinnerSize } from "baseComponents/Spinner/IPSpinner";

interface IKPIEvidenceTransformModalProps {
  transform: KPIDataTransform;
  isOpen: boolean;
  onClose: () => void;
  onSave: (transform: KPIDataTransform) => void;
  onRefresh: () => void;
}

const KPIEvidenceTransformModal = (props: IKPIEvidenceTransformModalProps) => {
  const appContext = React.useContext(AppContext);
  const { t } = useTranslation(['translation', 'kpis']);
  const [transform, setTransform] = React.useState<KPIDataTransform>(props.transform);
  const [arrayNode, setArrayNode] = React.useState<KeyPath | undefined>(undefined);
  const [selectedNode, setSelectedNode] = React.useState<KeyPath | undefined>(undefined);
  const [ignoredNodes, setIgnoredNodes] = React.useState<KeyPath[]>([]);
  const [extractedNode, setExtractedNode] = React.useState<KeyPath | undefined>(undefined);
  const [isTransforming, setIsTransforming] = React.useState<boolean>(false);
  const [transformResponses, setTransformResponses] = React.useState<KPIDataTransformResponse[]>([]);

  React.useEffect(() => {
    if (props.isOpen) {
      const arrayPath = getKeyPaths([props.transform.arrayPath])[0];
      const selectedPath = getKeyPaths([props.transform.selectedPath])[0];
      const ignoredPaths = getKeyPaths(props.transform.ignoredPaths);
      const extractedPath = getKeyPaths([props.transform.extractedPath])[0];

      //this will trigger the effect below to transform evidence
      setArrayNode(arrayPath);
      setSelectedNode(selectedPath);
      setIgnoredNodes([...ignoredPaths]);
      setExtractedNode(extractedPath);

      setTransform(props.transform);
    } else {
      setArrayNode(undefined);
      setSelectedNode(undefined);
      setIgnoredNodes([]);
      setExtractedNode(undefined);
      setTransformResponses([]);
    }
  }, [props.isOpen, props.transform]);

  React.useEffect(() => {
    if (props.isOpen) {
      transformEvidence();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedNode, arrayNode, ignoredNodes, extractedNode]);

  const getKeyPaths = (paths: (string | undefined)[] | undefined): KeyPath[] => {
    if (!paths) return [];
    const keyPaths: (string | number)[][] = [];

    for (let idx = 0; idx < paths.length; idx++) {
      const path = paths[idx];
      const splittedPath = path?.split('.');
      if (splittedPath && splittedPath.length > 0) {
        const keyPath: (string | number)[] = [];
        for (let partIdx = 0; partIdx < splittedPath?.length; partIdx++) {
          let part = splittedPath[partIdx];
          if (part.endsWith('[0]')) {
            part = part.substring(0, part.length - 3);
            keyPath.push(part);
            keyPath.push(0);
          } else {
            keyPath.push(part);
          }
        }
        keyPath.reverse();
        keyPaths.push(keyPath);
      }
    }

    return keyPaths;
  };

  const getJSonPaths = (paths: KeyPath[]): string[] => {
    const jsonPaths: string[] = [];

    for (let idx = 0; idx < paths.length; idx++) {
      const path = reverse(paths[idx] as (string | number)[]);
      let jsonPath = '';
      for (let partIdx = 0; partIdx < path.length; partIdx++) {
        const part = path[partIdx];
        if (typeof part === 'number') {
          jsonPath += '[' + part.toString() + ']';
        } else {
          if (jsonPath.length > 0) jsonPath += '.';
          jsonPath += part;
        }
      }
      jsonPaths.push(jsonPath);
    }

    return jsonPaths;
  };

  const reverse = (path: (string | number)[] | undefined): (string | number)[] => {
    if (!path) return [];

    return path?.slice()?.reverse() ?? [];
  };

  const transformEvidence = async () => {
    try {
      setIsTransforming(true);

      const accessToken = await appContext.getAccessToken(apiRequest.scopes);
      const newTransform = transform.clone();
      newTransform.arrayPath = getJSonPaths([arrayNode as (string | number)[]])[0];
      newTransform.selectedPath = getJSonPaths([selectedNode as (string | number)[]])[0];
      newTransform.ignoredPaths = getJSonPaths(ignoredNodes);
      newTransform.extractedPath = getJSonPaths([extractedNode as (string | number)[]])[0];

      const transformResponses = await apiApplyKPIDataTransform(newTransform, accessToken);
      setTransformResponses(transformResponses);
      setTransform(newTransform);
    } catch (err) {
      appContext.setError(err);
    } finally {
      setIsTransforming(false);
    }
  };

  const onSave = () => {
    if (transformResponses.length > 0) {
      transform.evidence = transformResponses[0].resultEvidence;
    }

    props.onSave(transform);
  };

  const onRefresh = () => {
    props.onRefresh();
  };

  //
  // Main render
  //
  return (
    <GenericModal
      title={t('kpi:TabEvidence.Dialogs.EvidenceTransform.Title')}
      isOpen={props.isOpen}
      onClose={props.onClose}
      onSave={onSave}
      minWidth={250}
      width={'60vw'}
      maxWidth={'95vw'}
      minHeight={300}
      height={'90vh'}
      maxHeight={'90vh'}
      hideFooter={false}
    >
      <IPSeparator />
      <IPStack verticalFill tokens={globalStackTokensGapMedium}>
        <IPText>{t('kpi:TabEvidence.Dialogs.EvidenceTransform.SubTitle')}</IPText>
        <IPStack.Item grow>
          <IPStack horizontal styles={{ root: { height: '100%' } }}>
            <IPStack verticalFill styles={{ root: { width: '50%' } }}>
              <IPStack.Item>
                <IPStack horizontal horizontalAlign="space-between" verticalAlign="center">
                  <IPStack.Item>
                    <IPText styles={globalTextStylesBold}>
                      {t('kpi:TabEvidence.Dialogs.EvidenceTransform.RawEvidence')}
                    </IPText>
                  </IPStack.Item>
                  <IPStack.Item>
                    <IPTooltipHost content={t('kpi:TabEvidence.Dialogs.EvidenceTransform.RawEvidenceRefresh')}>
                      <IPIconButton styles={{ root: { height: 15 } }} iconProps={refreshIcon} onClick={onRefresh} />
                    </IPTooltipHost>
                  </IPStack.Item>
                </IPStack>
                <IPSeparator />
              </IPStack.Item>
              <IPStack.Item grow styles={{ root: { position: 'relative' } }}>
                <IPScrollablePane>
                  <JSONViewer
                    data={transform?.rawEvidence}
                    hideRoot={false}
                    allowLabelSelect={true}
                    arrayNode={arrayNode}
                    selectedNode={selectedNode}
                    ignoredNodes={ignoredNodes}
                    extractedNode={extractedNode}
                    onUpdateSelectedNode={setSelectedNode}
                    onUpdateExtracedField={setExtractedNode}
                    onUpdateIgnoredNodes={setIgnoredNodes}
                    onUpdateArrayNode={setArrayNode}
                  />
                </IPScrollablePane>
              </IPStack.Item>
            </IPStack>
            <IPSeparator vertical />
            <IPStack verticalFill styles={{ root: { width: '50%' } }}>
              <IPStack.Item>
                <IPText styles={globalTextStylesBold}>
                  {t('kpi:TabEvidence.Dialogs.EvidenceTransform.EvidenceToApprove')}
                </IPText>
                <IPSeparator />
              </IPStack.Item>
              {isTransforming && (
                <IPStack verticalFill horizontalAlign="center" verticalAlign="center">
                  <IPSpinner size={IPSpinnerSize.large} />
                </IPStack>
              )}
              <IPStack.Item grow styles={{ root: { position: 'relative' } }}>
                {!isTransforming && (
                  <IPScrollablePane>
                    <ArrayViewer
                      data={transformResponses.map((t) => t.resultEvidence)}
                      child={JSONViewer}
                    ></ArrayViewer>
                  </IPScrollablePane>
                )}
              </IPStack.Item>
            </IPStack>
          </IPStack>
        </IPStack.Item>
      </IPStack>
    </GenericModal>
  );
};

export default KPIEvidenceTransformModal;
